/* eslint-disable */

const isFirefox = /firefox/i.test(navigator.userAgent);

const UPPER_REGEX = /[A-Z]/g;

// Don't convert these attributes from camelCase to hyphenated-attributes
const BLACKLIST = [
  'viewBox'
];

let keyCase = (key) => {
  if (!BLACKLIST.includes(key)) {
    key = key.replace(UPPER_REGEX, match => '-' + match.toLowerCase());
  }
  return key;
}

 export default {
  /**
   * The following methods are taken from mozilla/pdf.js and as such fall under
   * the Apache License (http://www.apache.org/licenses/).
   *
   * Original source can be found at mozilla/pdf.js:
   * https://github.com/mozilla/pdf.js/blob/master/web/ui_utils.js
   */

  /**
   * Approximates a float number as a fraction using Farey sequence (max order
   * of 8).
   *
   * @param {Number} x Positive float number
   * @return {Array} Estimated fraction: the first array item is a numerator,
   *                 the second one is a denominator.
   */
  approximateFraction : function(x) {
    // Fast path for int numbers or their inversions.
    if (Math.floor(x) === x) {
      return [x, 1];
    }

    const xinv = 1 / x;
    const limit = 8;
    if (xinv > limit) {
      return [1, limit];
    } else if (Math.floor(xinv) === xinv) {
      return [1, xinv];
    }

    const x_ = x > 1 ? xinv : x;
    
    // a/b and c/d are neighbours in Farey sequence.
    let a = 0, b = 1, c = 1, d = 1;
    
    // Limit search to order 8.
    while (true) {
      // Generating next term in sequence (order of q).
      let p = a + c, q = b + d;
      if (q > limit) {
        break;
      }
      if (x_ <= p / q) {
        c = p; d = q;
      } else {
        a = p; b = q;
      }
    }

    // Select closest of neighbours to x.
    if (x_ - a / b < c / d - x_) {
      return x_ === x ? [a, b] : [b, a];
    } else {
      return x_ === x ? [c, d] : [d, c];
    }
  },

  getOutputScale: function(ctx) {
    let devicePixelRatio = window.devicePixelRatio || 1;
    let backingStoreRatio = ctx.webkitBackingStorePixelRatio ||
                            ctx.mozBackingStorePixelRatio ||
                            ctx.msBackingStorePixelRatio ||
                            ctx.oBackingStorePixelRatio ||
                            ctx.backingStorePixelRatio || 1;
    let pixelRatio = devicePixelRatio / backingStoreRatio;
    return {
      sx: pixelRatio,
      sy: pixelRatio,
      scaled: pixelRatio !== 1
    };
  },

  roundToDivide: function(x, div) {
    let r = x % div;
    return r === 0 ? x : Math.round(x - r + div);
  },

    /**
   * Scale the elements of a page.
   *
   * @param {Number} pageNumber The page number to be scaled
   * @param {Object} viewport The viewport of the PDF page (see pdfPage.getViewport(scale, rotate))
   * @param {Object} context The canvas context that the PDF page is rendered to
   * @return {Array} The transform data for rendering the PDF page
   */
  scalePage: function(page, viewport, context) {
    let canvas = page.querySelector('.canvasWrapper canvas');
    let wrapper = page.querySelector('.canvasWrapper');
    let outputScale = this.getOutputScale(context);
    let transform = !outputScale.scaled ? null : [outputScale.sx, 0, 0, outputScale.sy, 0, 0];
    let sfx = this.approximateFraction(outputScale.sx);
    let sfy = this.approximateFraction(outputScale.sy);

    // Adjust width/height for scale
    page.style.visibility = '';
    canvas.width = this.roundToDivide(viewport.width * outputScale.sx, sfx[0]);
    canvas.height = this.roundToDivide(viewport.height * outputScale.sy, sfy[0]);
    canvas.style.width = this.roundToDivide(viewport.width, sfx[1]) + 'px';
    canvas.style.height = this.roundToDivide(viewport.height, sfx[1]) + 'px';
    page.style.width = `${viewport.width}px`;
    page.style.height = `${viewport.height}px`;

    return transform;
  },

  /**
   * Transform the rotation and scale of a node using SVG's native transform attribute.
   *
   * @param {Node} node The node to be transformed
   * @param {Object} viewport The page's viewport data
   * @return {Node}
   */
  transform: function(node, viewport) {
    let trans = this.getTranslation(viewport);

    // Let SVG natively transform the element
    node.setAttribute('transform', `scale(${viewport.scale}) rotate(${viewport.rotation}) translate(${trans.x}, ${trans.y})`);
    
    // Manually adjust x/y for nested SVG nodes
    if (!isFirefox && node.nodeName.toLowerCase() === 'svg') {
      node.setAttribute('x', Number.parseInt(node.getAttribute('x'), 10) * viewport.scale);
      node.setAttribute('y', Number.parseInt(node.getAttribute('y'), 10) * viewport.scale);

      let x = Number.parseInt(node.getAttribute('x', 10), 10);
      let y = Number.parseInt(node.getAttribute('y', 10), 10);
      let width = Number.parseInt(node.getAttribute('width'), 10);
      let height = Number.parseInt(node.getAttribute('height'), 10);
      let path = node.querySelector('path');
      let svg = path.parentNode;
     
      // Scale width/height
      for (const n of [node, svg, path, node.querySelector('rect')]) {
        n.setAttribute('width', Number.parseInt(n.getAttribute('width'), 10) * viewport.scale);
        n.setAttribute('height', Number.parseInt(n.getAttribute('height'), 10) * viewport.scale);
      }

      // Transform path but keep scale at 100% since it will be handled natively
      transform(path, objectAssign({}, viewport, { scale: 1 }));
      
      switch(viewport.rotation % 360) {
        case 90:
          node.setAttribute('x', viewport.width - y - width);
          node.setAttribute('y', x);
          svg.setAttribute('x', 1);
          svg.setAttribute('y', 0);
          break;
        case 180:
          node.setAttribute('x', viewport.width - x - width);
          node.setAttribute('y', viewport.height - y - height);
          svg.setAttribute('y', 2);
          break;
        case 270:
          node.setAttribute('x', y);
          node.setAttribute('y', viewport.height - x - height);
          svg.setAttribute('x', -1);
          svg.setAttribute('y', 0);
          break;
      }
    }

    return node;
  },

  /**
   * Get the x/y translation to be used for transforming the annotations
   * based on the rotation of the viewport.
   *
   * @param {Object} viewport The viewport data from the page
   * @return {Object}
   */
  getTranslation: function(viewport) {
    let x;
    let y;

    // Modulus 360 on the rotation so that we only
    // have to worry about four possible values.
    switch(viewport.rotation % 360) {
      case 0:
        x = y = 0;
        break;
      case 90:
        x = 0;
        y = (viewport.width / viewport.scale) * -1;
        break;
      case 180:
        x = (viewport.width / viewport.scale) * -1;
        y = (viewport.height / viewport.scale) * -1;
        break;
      case 270:
        x = (viewport.height / viewport.scale) * -1;
        y = 0;
        break;
    }

    return { x, y };
  },

  /**
   * Set attributes for a node from a map
   *
   * @param {Node} node The node to set attributes on
   * @param {Object} attributes The map of key/value pairs to use for attributes
   */
  setAttributes: function(node, attributes) {
    for (const key of Object.keys(attributes)) {
      node.setAttribute(keyCase(key), attributes[key]);
    }
  },

  getViewport: function(pdfPage, width, height, zoomScale = "1.0") {
    const unscaledViewport = pdfPage.getViewport(1, pdfPage.rotate);
    let localScale = 1;

    localScale = Math.min(width, height) / Math.min(unscaledViewport.width, unscaledViewport.height);
    
    const localViewport = pdfPage.getViewport(localScale, pdfPage.rotate);
    const output = pdfPage.getViewport(localViewport.scale, localViewport.rotation);
    return output;
  },

  /**
   * Build the print-time @page style (and, for fit modes, a zoom factor) from
   * the actually-rendered page composites. Fixes the non-standard-page-size
   * print/export bug: instead of a hardcoded Letter/A4 sheet, the sheet is
   * derived from the content so the browser does not rescale/reflow the cue
   * overlay away from the on-screen layout.
   *
   * Caller MUST pass the popup document's `.ql-pages-inner` elements in — this
   * helper never queries the DOM itself (Utils is also imported in the main
   * window; a self-query could read the wrong document).
   *
   * Fidelity is guaranteed for Chrome -> Save as PDF with Margins: None and
   * Scale: 100%. On a physical printer / default margins the driver refits the
   * sheet to the media and reintroduces scaling — that is what the fit modes
   * are for.
   *
   * @param {String} mode 'screen' (default) | 'letter' | 'a4'
   * @param {Array<Element>} pageEls the rendered `.ql-pages-inner` elements
   * @return {{css: String, zoom: Number}} injectable @page CSS + zoom (1 unless fitting)
   */
  buildPrintPageStyle: function(mode, pageEls) {
    const CSS_PPI = 96;
    // Standard sheets in CSS px @96dpi (portrait).
    const SHEETS = {
      letter: { w: 816, h: 1056 },
      a4: { w: 794, h: 1123 }
    };

    // Chrome cannot vary @page size per page, so one global sheet is the only
    // option; take the max composite so the largest page is never clipped.
    let maxW = 0;
    let maxH = 0;
    const els = pageEls || [];
    for (let i = 0; i < els.length; i++) {
      const el = els[i];
      if (!el || typeof el.getBoundingClientRect !== 'function') continue;
      const r = el.getBoundingClientRect();
      if (r.width > maxW) maxW = r.width;
      if (r.height > maxH) maxH = r.height;
    }

    // Guard an unsettled/partial DOM: never emit a zero-size @page (blank print).
    if (!(maxW > 0) || !(maxH > 0)) {
      return { css: '@media print { @page { size: Letter; margin: 0; } }', zoom: 1 };
    }

    if (mode === 'letter' || mode === 'a4') {
      const sheet = SHEETS[mode];
      // Orientation follows the content so a wide composite uses a landscape sheet.
      const landscape = maxW > maxH;
      const sheetW = landscape ? sheet.h : sheet.w;
      const sheetH = landscape ? sheet.w : sheet.h;
      // Uniform fit via `zoom` (applied by the caller). Unlike `transform`,
      // `zoom` changes the layout box, so pagination / page-break stay correct
      // and the overlay scales uniformly with the PDF (same subtree). Never
      // upscale past 1 (a small page shouldn't be blown up to fill the sheet).
      const zoom = Math.min(sheetW / maxW, sheetH / maxH, 1);
      const orient = landscape ? ' landscape' : ' portrait';
      const named = mode === 'a4' ? 'A4' : 'Letter';
      return {
        css: `@media print { @page { size: ${named}${orient}; margin: 0; } }`,
        zoom: zoom
      };
    }

    // 'screen' (default): size the sheet to the content at 96dpi so 1 CSS px =
    // 1/96in and the browser applies no scaling — a pixel-exact copy of the app
    // view for any PDF size.
    const wIn = (maxW / CSS_PPI).toFixed(3);
    const hIn = (maxH / CSS_PPI).toFixed(3);
    return {
      css: `@media print { @page { size: ${wIn}in ${hIn}in; margin: 0; } }`,
      zoom: 1
    };
  },

  cueAttributes: {
    label: 'Label',
    notes: 'Notes',
    description: 'Description',
    parts: 'Parts',
    time: 'Time',
    focus: 'Focus',
    color: 'Color',
    beam: 'Beam',
    mark: 'Mark',
    duration: 'Duration',
    followHang: 'Follow/Hang',
    block: 'Block',
    assert: 'Assert',
    link: 'Link',
    loop: 'Loop',
    curve: 'Curve',
    externalLinks: 'External Links',
    target: 'Target',
    preWait: 'Pre Wait',
    action: 'Action',
    postWait: 'Post Wait',
    placement: 'Placement',
    purpose: 'Purpose',
    followMode: 'Follow Mode',
    routing: 'Routing',
    trigger: 'Trigger',
    propName: 'Prop Name',
    propQuantity: 'Prop Quantity',
    preset: 'Preset',
    whoEnter: 'Who Enter',
    whereEnter: 'Where Enter',
    whoExit: 'Who Exit',
    whereExit: 'Where Exit',
    exitPage: 'Exit Page',
    propNotes: 'Prop Notes',
    consumable: 'Consumable',
    rehearsal: 'Rehearsal',
    rehearsalSource: 'Rehearsal Source',
    actual: 'Actual',
    actualSource: 'Actual Source',
    user1: 'User Defined 1',
    user2: 'User Defined 2',
    user3: 'User Defined 3',
    user4: 'User Defined 4',
    workNote: 'Work Note',
  },

  // Auto-numbering helpers -------------------------------------------------
  // Round an auto-number value to strip binary float drift (e.g. 0.1 + 0.2 ->
  // 0.30000000000000004) AND trailing zeros. Number(toFixed(6)) does both.
  roundCueNumber: function(n) {
    const num = Number(n);
    if (!Number.isFinite(num)) return num;
    return Number(num.toFixed(6));
  },

  // Shared validation for the "Starting Number" seed: any finite number.
  isValidCueNumberSeed: function(v) {
    if (v === '' || v === null || v === undefined) return false;
    return Number.isFinite(Number(v));
  },

  // Shared validation for the "Interval": a finite number strictly > 0.
  // (Rejects 0 and negatives, which would produce duplicate / decrementing
  // cue numbers.)
  isValidCueInterval: function(v) {
    if (v === '' || v === null || v === undefined) return false;
    const num = Number(v);
    return Number.isFinite(num) && num > 0;
  }

}


// WEBPACK FOOTER //
// ./src/lib/Utils.js